import type {
	PaymentError,
	PaymentErrorHandler,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { useCompletePayment } from "../../hooks/useCompletePayment";
import { useElavon } from "../../hooks/useElavon";
type ElavonLightboxFormProps = {
	paymentSession: NonNullable<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"]>;
	formId: string;
	onError: PaymentErrorHandler;
	onSuccess: (publicIdentifier: string) => void;
	setIsProcessing: (isProcessing: boolean) => void;
};
export const ElavonLightboxForm = ({
	paymentSession,
	formId,
	onSuccess,
	onError,
	setIsProcessing,
}: ElavonLightboxFormProps) => {
	const { isReady, openLightbox } = useElavon({
		paymentSession,
	});
	// The Converge transaction token is good for one lightbox transaction, so every exit other than
	// success needs a new session before the next Pay.
	const failAttempt = (error: PaymentError) => onError({ error, needsNewSession: true });
	const { mutate: completePayment } = useCompletePayment({
		platform: "Elavon",
		onSuccess,
		onError: ({ error }) => failAttempt(error),
	});
	const handleOpenLightbox = () => {
		if (!isReady) {
			return;
		}
		setIsProcessing(true);
		openLightbox({
			onApproval: (response) => {
				completePayment({
					paymentSessionId: paymentSession.paymentId,
					token: response.ssl_txn_id,
				});
			},
			// Verify declined transaction IDs server-side so the decline is recorded.
			onDeclined: (response) => {
				completePayment({
					paymentSessionId: paymentSession.paymentId,
					token: response.ssl_txn_id,
				});
			},
			onError: (error) => {
				if (error.errorCode === "NOT_LOADED") {
					setIsProcessing(false);
					return;
				}
				failAttempt({ type: "unconfirmed" });
			},
			onCancelled: () => failAttempt({ type: "not-charged" }),
		});
	};
	return (
		<form
			id={formId}
			onSubmit={(e) => {
				e.preventDefault();
				handleOpenLightbox();
			}}
		/>
	);
};
